import React, { useState, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * The basketball box score, shared by live scoring and the finished-game view.
 *
 * Both screens were rendering the same numbers from their own markup, which is
 * how they drifted: one summed named players' points as the team score while
 * the other showed the real one. There is a single table now, fed the same
 * `boxScore` the server derives from the possession log, so the two cannot
 * disagree again.
 *
 * Ordered by points, highest first — the question anyone actually asks of a box
 * score is who scored, not who was listed first on the teamsheet.
 *
 * @param {object} boxScore  playerId -> per-player totals, as the API returns them
 * @param {Array}  teams     [{ name, players: [{id, name}] }] — drives the filter
 *                           and tells each row which side it belongs to
 * @param {Array}  [periodScores] [{ period, a, b }] for the quarter line
 * @param {boolean} [compact] tighter type, for the live scoring panel
 */
const BoxScore = ({ boxScore = {}, teams = [], periodScores = [], compact = false }) => {
  const { theme } = useTheme();
  const [teamFilter, setTeamFilter] = useState('all');

  const rows = useMemo(() => {
    const teamOf = new Map();
    teams.forEach(t => (t.players || []).forEach(p => teamOf.set(String(p.id), t.name)));
    const nameOf = new Map();
    teams.forEach(t => (t.players || []).forEach(p => nameOf.set(String(p.id), p.name)));

    return Object.entries(boxScore)
      .map(([id, s]) => ({
        id,
        name: nameOf.get(String(id)) || 'Unknown',
        team: teamOf.get(String(id)) || null,
        ...s,
        fga: (s.two_pointers_made + s.two_pointers_missed) + (s.three_pointers_made + s.three_pointers_missed),
        fgm: s.two_pointers_made + s.three_pointers_made,
      }))
      // Points first, then rebounds, then assists — a stable, meaningful order
      // rather than whatever order the object happened to be built in.
      .sort((a, b) => b.total_points - a.total_points || b.rebounds - a.rebounds || b.assists - a.assists);
  }, [boxScore, teams]);

  const visible = teamFilter === 'all' ? rows : rows.filter(r => r.team === teamFilter);
  if (rows.length === 0) return null;

  // The leading scorer, but only when someone is genuinely ahead — crowning a
  // five-way tie on zero points would be noise.
  const top = rows[0]?.total_points > 0 && rows[0].total_points > (rows[1]?.total_points ?? -1)
    ? rows[0].id
    : null;

  const pct = (made, att) => (att > 0 ? `${Math.round((made / att) * 100)}%` : '—');
  const cell = compact ? 'py-1 px-1 text-center' : 'py-2 px-1 text-center';

  const totals = visible.reduce((a, r) => ({
    total_points: a.total_points + r.total_points,
    rebounds: a.rebounds + r.rebounds, assists: a.assists + r.assists,
    steals: a.steals + r.steals, blocks: a.blocks + r.blocks,
    turnovers: a.turnovers + (r.turnovers ?? 0),
    fgm: a.fgm + r.fgm, fga: a.fga + r.fga,
  }), { total_points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, turnovers: 0, fgm: 0, fga: 0 });

  return (
    <div>
      {teams.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {[{ key: 'all', label: 'All players' }, ...teams.map(t => ({ key: t.name, label: t.name }))].map(opt => (
            <button
              key={opt.key}
              onClick={() => setTeamFilter(opt.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                teamFilter === opt.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : `${theme.border} ${theme.textMuted}`
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead>
            <tr className={`${theme.textMuted} text-xs border-b ${theme.border}`}>
              <th className="text-left py-2 font-medium">Player</th>
              <th className="py-2 px-1 font-medium">PTS</th>
              <th className="py-2 px-1 font-medium">FG</th>
              <th className="py-2 px-1 font-medium">3PT</th>
              <th className="py-2 px-1 font-medium">FT</th>
              <th className="py-2 px-1 font-medium">REB</th>
              <th className="py-2 px-1 font-medium">AST</th>
              <th className="py-2 px-1 font-medium">STL</th>
              <th className="py-2 px-1 font-medium">BLK</th>
              {/* TO sits here because the game card above already reports team
                  turnovers — a sheet that shows them for the side but never for
                  a player reads as broken, which is exactly how it was read. */}
              <th className="py-2 px-1 font-medium">TO</th>
              <th className="py-2 px-1 font-medium">PF</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.id} className={`${theme.text} border-b ${theme.border} last:border-0`}>
                <td className={`text-left ${compact ? 'py-1' : 'py-2'} pr-2`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {r.id === top && <span title="Top scorer" aria-label="Top scorer">🏀</span>}
                    <span className="truncate max-w-[9rem]">{r.name}</span>
                  </div>
                  {teamFilter === 'all' && teams.length > 1 && r.team && (
                    <span className={`text-[10px] ${theme.textMuted} truncate block`}>{r.team}</span>
                  )}
                </td>
                <td className={`${cell} font-bold text-base`}>{r.total_points}</td>
                <td className={cell}>
                  {r.fgm}/{r.fga}
                  <span className={`block text-[10px] ${theme.textMuted}`}>{pct(r.fgm, r.fga)}</span>
                </td>
                <td className={cell}>{r.three_pointers_made}/{r.three_pointers_made + r.three_pointers_missed}</td>
                <td className={cell}>{r.free_throws_made}/{r.free_throws_attempts}</td>
                <td className={cell}>{r.rebounds}</td>
                <td className={cell}>{r.assists}</td>
                <td className={cell}>{r.steals}</td>
                <td className={cell}>{r.blocks}</td>
                <td className={cell}>{r.turnovers ?? 0}</td>
                <td className={cell}>{r.personal_fouls}</td>
              </tr>
            ))}
          </tbody>
          {visible.length > 1 && (
            <tfoot>
              <tr className={`${theme.textSecondary} text-xs font-semibold`}>
                <td className="text-left py-2">
                  {teamFilter === 'all' ? 'All players' : teamFilter}
                </td>
                <td className={cell}>{totals.total_points}</td>
                <td className={cell}>{totals.fgm}/{totals.fga}</td>
                <td className={cell} colSpan={2} />
                <td className={cell}>{totals.rebounds}</td>
                <td className={cell}>{totals.assists}</td>
                <td className={cell}>{totals.steals}</td>
                <td className={cell}>{totals.blocks}</td>
                <td className={cell}>{totals.turnovers}</td>
                <td className={cell} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {periodScores.length > 0 && (
        <div className={`mt-3 flex flex-wrap gap-2 text-xs ${theme.textMuted}`}>
          {periodScores.map(p => (
            <span key={p.period} className={`px-2 py-1 rounded border ${theme.border}`}>
              Q{p.period} <span className={theme.text}>{p.a}–{p.b}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BoxScore;
